import { useQueries } from '@tanstack/react-query';
import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ChevronDown, CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetClass } from '~entities/classes/api/get-class';
import {
  getEnrollmentTotalPayments,
  useGetEnrollmentTotalPayments,
} from '~entities/classes/api/get-enrollment-total-payments';
import {
  getPreviewLineScheduledSuffix,
  isAlreadyEnrolledNoop,
  type PreviewChargeBreakdown,
  usePreviewEnrollment,
} from '~entities/enrollment/api/preview-enrollment';
import {
  getStartEnrollmentOutcome,
  useStartEnrollment,
} from '~entities/enrollment/api/start-enrollment';
import { getFamilyByStudent } from '~entities/family';
import { getParentById } from '~entities/parent/api/get-parent-by-id';
import {
  type OnlinePaymentMethod,
  usePaymentMethodOptions,
} from '~entities/payment/api/get-allowed-payment-methods';
import {
  getEnrollmentPaymentMethods,
  pickEnrollmentCatalogDefaultMethod,
} from '~entities/payment/api/get-enrollment-payment-methods';
import { resolveBulkEnrollmentPaymentMethods } from '~entities/payment/api/resolve-bulk-enrollment-payment-methods';
import { getSessionFeeStatus } from '~entities/session/api/get-session-fee-status';
import {
  getAddStudentToEntityOutcome,
  useAddStudentToEntity,
} from '~entities/stripe/api/add-student-to-class';
import { getStudentById } from '~entities/student/api/get-student-by-id';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { PayingParentSelect } from '~features/enrollment/ui/paying-parent-select';
import { ConfirmationStep } from '~features/payment/ui/confirmation-step';
import { StudentSelectionStep } from '~features/payment/ui/student-selection-step';
import {
  resolveHourlyLastMonthPreviewAmount,
  sumHourlyLastMonthPreviewAmounts,
} from '~features/students/utils/hourly-last-month-preview';

import { useGetCurrency } from '~shared/api/currency-api';
import { KEYS } from '~shared/api/query-keys';
import { formatBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~shared/shadcn/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { Separator } from '~shared/shadcn/ui/separator';
import { calculateProratedTuition } from '~shared/utils/proration';

export interface Student {
  _id: string;
  name: string;
  familyName?: string;
  parentId?: string;
}

const formatLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalDateKey = (dateValue?: string | Date | null) => {
  if (!dateValue) return null;

  if (typeof dateValue === 'string') {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const localDate = new Date(Number(year), Number(month) - 1, Number(day));
      const isValidDate =
        localDate.getFullYear() === Number(year) &&
        localDate.getMonth() === Number(month) - 1 &&
        localDate.getDate() === Number(day);

      return isValidDate ? `${year}-${month}-${day}` : null;
    }
  }

  const parsedDate =
    dateValue instanceof Date ? dateValue : new Date(dateValue);
  return Number.isNaN(parsedDate.getTime())
    ? null
    : formatLocalDateKey(parsedDate);
};

const getRuntimeSession = <TSession,>(entity?: { session?: TSession } | null) =>
  entity?.session;

/** Shorten fee labels like "Tuition — BC4 — …" → keep tuition wording intact.
 * Non-tuition fee labels still collapse to the primary segment. */
const shortenPreviewLineLabel = (label: string) => {
  if (/\btuition\b/i.test(label)) {
    return label;
  }
  const primary = label.split('—')[0]?.trim();
  return primary || label;
};

interface BulkEnrollStudentsProps {
  classId: string;
}

export const BulkEnrollStudents = ({ classId }: BulkEnrollStudentsProps) => {
  const { data: classData } = useGetClass({
    classId,
  });
  const { data: studioDetails } = useGetStudioDetails();
  const { data: currencyData } = useGetCurrency();

  const isV2Enrollment = studioDetails?.isV2 === true;

  const isHourlyLastMonthCharge = useMemo(
    () =>
      classData
        ? classData.tuitionBillingCycle === 'hourly' &&
          !!getRuntimeSession(classData)?.chargeLastMonthAtCheckout
        : false,
    [classData],
  );

  const studioId = studioDetails?._id;

  const { data: totalPaymentsData } = useGetEnrollmentTotalPayments(
    { enrollmentId: classId },
    {
      enabled: !!classId && !isHourlyLastMonthCharge && !isV2Enrollment,
    },
  );

  const router = useRouter();
  const canGoBack = useCanGoBack();
  const locationId = studioDetails?.locationId ?? '';
  const { firstAllowedMethod } = usePaymentMethodOptions(locationId);

  useEffect(() => {
    if (!document.getElementById('custom-animations')) {
      const style = document.createElement('style');
      style.id = 'custom-animations';
      style.innerHTML = `
        @keyframes slideDownAndFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-slide-down {
          animation: slideDownAndFade 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const [step, setStep] = useState<'selection' | 'confirmation'>('selection');
  const [selectedStudents, setSelectedStudents] = useState<Array<Student>>([]);
  const [showEnrollmentDialog, setShowEnrollmentDialog] = useState(false);
  const [skipPaymentMethod, setSkipPaymentMethod] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    OnlinePaymentMethod | undefined
  >(undefined);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [resolvedPaymentRows, setResolvedPaymentRows] = useState<
    Array<{ studentId: string; paymentMethodId: string }>
  >([]);
  const [payingParentByStudent, setPayingParentByStudent] = useState<
    Record<string, string | undefined>
  >({});
  const [paymentMethodPreloadStatus, setPaymentMethodPreloadStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [paymentMethodPreloadError, setPaymentMethodPreloadError] = useState<
    string | null
  >(null);
  const [enrollmentPreview, setEnrollmentPreview] = useState<{
    amountDueTotal: number;
    charges: PreviewChargeBreakdown[];
  } | null>(null);

  useEffect(() => {
    if (skipPaymentMethod) return;
    if (!firstAllowedMethod) return;
    if (paymentMethod !== firstAllowedMethod) {
      setPaymentMethod(firstAllowedMethod);
    }
  }, [skipPaymentMethod, firstAllowedMethod, paymentMethod]);

  const shouldFetchHourlyLastMonthPreview =
    !isV2Enrollment &&
    isHourlyLastMonthCharge &&
    !!studioId &&
    selectedStudents.length > 0 &&
    (step === 'confirmation' || showEnrollmentDialog);

  const hourlyLastMonthPaymentQueries = useQueries({
    queries: selectedStudents.map((student) => ({
      queryKey: [
        KEYS.ENROLLMENT_TOTAL_PAYMENTS,
        classId,
        student._id,
        studioId,
        'bulk-enroll-hourly-last-month',
      ],
      queryFn: () =>
        getEnrollmentTotalPayments({
          enrollmentId: classId,
          studentId: student._id,
          studioId: studioId ?? '',
        }),
      enabled: shouldFetchHourlyLastMonthPreview,
    })),
  });

  const perStudentLastMonthTuition = useMemo(
    () =>
      selectedStudents.map((student, index) => {
        const query = hourlyLastMonthPaymentQueries[index];
        const amount = resolveHourlyLastMonthPreviewAmount(
          query?.data,
          classId,
        );
        return {
          studentId: student._id,
          name: student.name,
          amount,
          isLoading: query?.isLoading ?? false,
          isError: query?.isError ?? false,
        };
      }),
    [selectedStudents, hourlyLastMonthPaymentQueries, classId],
  );

  const isHourlyLastMonthPreviewLoading =
    !isV2Enrollment &&
    isHourlyLastMonthCharge &&
    hourlyLastMonthPaymentQueries.some(
      (query) => query.isLoading || query.isFetching,
    );

  const hasHourlyLastMonthPreviewError =
    !isV2Enrollment &&
    isHourlyLastMonthCharge &&
    hourlyLastMonthPaymentQueries.some((query) => query.isError);

  const hourlyLastMonthTuitionTotal = useMemo(() => {
    if (isV2Enrollment || !isHourlyLastMonthCharge) {
      return undefined;
    }
    return sumHourlyLastMonthPreviewAmounts(
      perStudentLastMonthTuition.map((row) => row.amount),
      !isHourlyLastMonthPreviewLoading && !hasHourlyLastMonthPreviewError,
    );
  }, [
    isV2Enrollment,
    isHourlyLastMonthCharge,
    perStudentLastMonthTuition,
    isHourlyLastMonthPreviewLoading,
    hasHourlyLastMonthPreviewError,
  ]);

  const isProrationActive = useMemo(() => {
    if (!classData || isV2Enrollment) return false;
    if (studioDetails?.prorationEnabled === false) return false;
    if (!classData.prorateTuition) return false;
    if (classData.tuitionBillingCycle === 'one-time') return false;
    return true;
  }, [classData, studioDetails?.prorationEnabled, isV2Enrollment]);

  const effectiveTuition = useMemo(() => {
    if (!classData || isV2Enrollment) return 0;
    const session = getRuntimeSession(classData);
    const fullTuition = classData.tuitionFee;
    const payments = totalPaymentsData?.payments;
    const todayStr = formatLocalDateKey(new Date());
    const billingDateStr = getLocalDateKey(session?.billingDate);
    const isScheduled = !!billingDateStr && todayStr < billingDateStr;
    const chargeLastMonth = isScheduled && !!session?.chargeLastMonthAtCheckout;

    if (payments?.length) {
      const checkoutPayment = chargeLastMonth
        ? payments[payments.length - 1]
        : payments[0];
      if (checkoutPayment) {
        return checkoutPayment.amount;
      }
    }

    if (!isProrationActive || chargeLastMonth) return fullTuition;

    const backendFirstPayment = totalPaymentsData?.payments?.[0];
    if (backendFirstPayment?.isProrated) {
      return backendFirstPayment.amount;
    }

    const proratedAmount = calculateProratedTuition({
      tuitionFee: fullTuition,
      tuitionBillingCycle: classData.tuitionBillingCycle,
      startDate: classData.startDate,
      endDate: classData.endDate,
      billingDay: classData.billingDay,
      billingFrequencyDays: classData.billingFrequencyDays,
    });
    return proratedAmount;
  }, [
    classData,
    totalPaymentsData?.payments,
    isProrationActive,
    isV2Enrollment,
  ]);

  const isProrated = useMemo(() => {
    if (!classData || isV2Enrollment) return false;
    return isProrationActive && effectiveTuition < classData.tuitionFee;
  }, [classData, effectiveTuition, isProrationActive, isV2Enrollment]);

  const sessionId = getRuntimeSession(classData)?._id;

  const selectedStudentCount = selectedStudents.length;
  const perStudentClassFeesTotal = useMemo(() => {
    if (!classData || selectedStudentCount === 0) {
      return 0;
    }
    return (
      ((classData.costumeFee ?? 0) + classData.registrationFeeAmount) *
      selectedStudentCount
    );
  }, [classData, selectedStudentCount]);

  const studentsMissingParentId = useMemo(
    () => selectedStudents.filter((student) => !student.parentId),
    [selectedStudents],
  );

  const parentIdQueries = useQueries({
    queries: studentsMissingParentId.map((student) => ({
      queryKey: [KEYS.STUDENTS, student._id, 'bulk-enroll-parentId'],
      queryFn: () => getStudentById(student._id),
      enabled: step === 'confirmation',
    })),
  });

  const parentIdByStudentId = useMemo(() => {
    const map = new Map<string, string>();
    selectedStudents.forEach((student) => {
      if (student.parentId) {
        map.set(student._id, student.parentId);
      }
    });
    studentsMissingParentId.forEach((student, index) => {
      const parentId = parentIdQueries[index]?.data?.parentId;
      if (parentId) {
        map.set(student._id, parentId);
      }
    });
    return map;
  }, [selectedStudents, studentsMissingParentId, parentIdQueries]);

  const sessionFeeQueries = useQueries({
    queries: selectedStudents.map((student) => {
      const parentId = parentIdByStudentId.get(student._id);

      return {
        queryKey: [
          KEYS.SESSION_FEE_STATUS,
          sessionId,
          parentId,
          student._id,
          'bulk-enroll',
        ],
        queryFn: () => {
          if (!sessionId || !parentId) {
            throw new Error('Session and parent are required');
          }

          return getSessionFeeStatus({
            sessionId,
            parentId,
            studentId: student._id,
          });
        },
        enabled:
          !isV2Enrollment && showEnrollmentDialog && !!sessionId && !!parentId,
      };
    }),
  });

  const sessionRegistrationFees = useMemo(() => {
    let perStudentTotal = 0;
    let perFamilyTotal = 0;
    const perFamilyParentIds = new Set<string>();

    sessionFeeQueries.forEach((query, index) => {
      const data = query.data;
      const student = selectedStudents[index];
      if (!data || !student) return;

      if (!data.isPerStudentFeePaid) {
        perStudentTotal += data.perStudentFee;
      }

      const parentId = parentIdByStudentId.get(student._id);
      if (
        parentId &&
        !data.isPerFamilyFeePaid &&
        !perFamilyParentIds.has(parentId)
      ) {
        perFamilyParentIds.add(parentId);
        perFamilyTotal += data.perFamilyFee;
      }
    });

    return {
      perStudentTotal,
      perFamilyTotal,
      total: perStudentTotal + perFamilyTotal,
      isLoading:
        parentIdQueries.some((query) => query.isLoading) ||
        sessionFeeQueries.some((query) => query.isLoading),
    };
  }, [
    sessionFeeQueries,
    parentIdQueries,
    selectedStudents,
    parentIdByStudentId,
  ]);

  const classDataWithOptionalSession = classData as
    | {
        session?: {
          billingDate?: string;
          chargeLastMonthAtCheckout?: boolean;
          chargeSessionFeesAtEnrollment?: boolean;
        };
      }
    | undefined;

  const isSessionBillingDateFuture = useMemo(() => {
    const todayStr = formatLocalDateKey(new Date());
    const billingDateStr = getLocalDateKey(
      classDataWithOptionalSession?.session?.billingDate,
    );
    return !!billingDateStr && todayStr < billingDateStr;
  }, [classDataWithOptionalSession?.session?.billingDate]);

  const isClassPaymentScheduled = classData
    ? isSessionBillingDateFuture &&
      !classDataWithOptionalSession?.session?.chargeLastMonthAtCheckout
    : false;

  const areSessionFeesScheduled = classData
    ? isClassPaymentScheduled &&
      !classDataWithOptionalSession?.session?.chargeSessionFeesAtEnrollment
    : false;

  const amountDueNow = useMemo(() => {
    if (!classData || isV2Enrollment) return 0;

    const sessionFeeTotal = areSessionFeesScheduled
      ? 0
      : sessionRegistrationFees.total;

    if (isClassPaymentScheduled) {
      return Math.max(0, sessionFeeTotal);
    }

    if (isHourlyLastMonthCharge) {
      const tuitionTotal = hourlyLastMonthTuitionTotal ?? 0;
      return Math.max(
        0,
        tuitionTotal + perStudentClassFeesTotal + sessionFeeTotal,
      );
    }

    const classFeeTotal =
      effectiveTuition +
      (classData.costumeFee ?? 0) +
      classData.registrationFeeAmount;

    return Math.max(0, classFeeTotal + sessionFeeTotal);
  }, [
    areSessionFeesScheduled,
    classData,
    effectiveTuition,
    hourlyLastMonthTuitionTotal,
    isClassPaymentScheduled,
    isHourlyLastMonthCharge,
    isV2Enrollment,
    perStudentClassFeesTotal,
    sessionRegistrationFees.total,
  ]);

  const displayCostumeFee = isHourlyLastMonthCharge
    ? (classData?.costumeFee ?? 0) * selectedStudentCount
    : (classData?.costumeFee ?? 0);

  const displayMembershipFee = isHourlyLastMonthCharge
    ? (classData?.registrationFeeAmount ?? 0) * selectedStudentCount
    : (classData?.registrationFeeAmount ?? 0);

  const hourlyLastMonthTotalPayments =
    hourlyLastMonthPaymentQueries[0]?.data?.totalPayments;

  const formatAmount = (amount: number, isScheduled = false) => {
    return `${currencyData?.symbol ?? ''}${amount.toFixed(2)}${
      isScheduled && amount > 0 ? ' (Scheduled)' : ''
    }`;
  };

  const enrollMutation = useStartEnrollment();
  const previewEnrollmentMutation = usePreviewEnrollment();
  const addStudentToEntityMutation = useAddStudentToEntity();

  const handleStudentsSelected = (students: Array<Student>) => {
    setSelectedStudents(students);
    setPayingParentByStudent({});
    setStep('confirmation');
  };

  const handleBack = () => {
    setStep('selection');
    setEnrollmentPreview(null);
    setResolvedPaymentRows([]);
    setPayingParentByStudent({});
    setPaymentMethodPreloadStatus('idle');
    setPaymentMethodPreloadError(null);
  };

  const selectedStudentIdsKey = useMemo(
    () => selectedStudents.map((student) => student._id).join(','),
    [selectedStudents],
  );

  const payingParentKey = useMemo(
    () =>
      selectedStudents
        .map((student) => `${student._id}:${payingParentByStudent[student._id] ?? ''}`)
        .join('|'),
    [selectedStudents, payingParentByStudent],
  );

  useEffect(() => {
    if (!isV2Enrollment || step !== 'confirmation') {
      return;
    }

    if (skipPaymentMethod) {
      setResolvedPaymentRows([]);
      setPaymentMethodPreloadError(null);
      setPaymentMethodPreloadStatus('ready');
      return;
    }

    if (!selectedStudentIdsKey) {
      return;
    }

    const studentIds = selectedStudentIdsKey.split(',').filter(Boolean);
    let cancelled = false;
    setPaymentMethodPreloadStatus('loading');
    setPaymentMethodPreloadError(null);
    setResolvedPaymentRows([]);
    setEnrollmentPreview(null);

    const preload = async () => {
      try {
        const rows = await Promise.all(
          studentIds.map(async (studentId) => {
            let parentId = payingParentByStudent[studentId];
            if (!parentId) {
              try {
                const familyPayload = await getFamilyByStudent(studentId);
                parentId =
                  familyPayload.family?.billingParentId ||
                  familyPayload.student.parentId ||
                  undefined;
              } catch {
                parentId = undefined;
              }
            }
            if (!parentId) {
              return {
                studentId,
                error: 'Could not resolve paying parent for student.',
              };
            }
            const catalog = await getEnrollmentPaymentMethods(parentId);
            const defaultMethod = pickEnrollmentCatalogDefaultMethod(catalog);
            if (!defaultMethod) {
              return {
                studentId,
                error: 'No saved payment method for paying parent.',
              };
            }
            return {
              studentId,
              paymentMethodId: defaultMethod.id,
            };
          }),
        );
        if (cancelled) return;

        const failed = rows.find((row) => 'error' in row && row.error);
        if (failed && 'error' in failed) {
          setResolvedPaymentRows([]);
          setPaymentMethodPreloadError(
            failed.error ??
              'Failed to load payment methods for selected students.',
          );
          setPaymentMethodPreloadStatus('error');
          return;
        }

        setResolvedPaymentRows(
          rows.map((row) => ({
            studentId: row.studentId,
            paymentMethodId: (row as { paymentMethodId: string })
              .paymentMethodId,
          })),
        );
        setPaymentMethodPreloadStatus('ready');
      } catch (error) {
        if (cancelled) return;
        // Fall back to legacy bulk resolve when family/PM path fails.
        if (!locationId) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to load payment methods for selected students.';
          setResolvedPaymentRows([]);
          setPaymentMethodPreloadError(message);
          setPaymentMethodPreloadStatus('error');
          return;
        }
        try {
          const { results } = await resolveBulkEnrollmentPaymentMethods({
            locationId,
            studentIds,
          });
          if (cancelled) return;
          const failed = results.find(
            (row) => row.error || !row.paymentMethodId,
          );
          if (failed) {
            setResolvedPaymentRows([]);
            setPaymentMethodPreloadError(
              failed.error ??
                'Failed to load payment methods for selected students.',
            );
            setPaymentMethodPreloadStatus('error');
            return;
          }
          setResolvedPaymentRows(
            results.map((row) => ({
              studentId: row.studentId,
              paymentMethodId: row.paymentMethodId as string,
            })),
          );
          setPaymentMethodPreloadStatus('ready');
        } catch (fallbackError) {
          if (cancelled) return;
          const message =
            fallbackError instanceof Error
              ? fallbackError.message
              : 'Failed to load payment methods for selected students.';
          setResolvedPaymentRows([]);
          setPaymentMethodPreloadError(message);
          setPaymentMethodPreloadStatus('error');
        }
      }
    };

    void preload();
    return () => {
      cancelled = true;
    };
  }, [
    isV2Enrollment,
    payingParentKey,
    step,
    skipPaymentMethod,
    locationId,
    selectedStudentIdsKey,
  ]);

  /** V2: preview first. V1: open confirm dialog immediately. */
  const handleConfirm = async () => {
    if (!studioDetails?._id) {
      toast.error('Studio details not found');
      return;
    }

    if (!isV2Enrollment) {
      setShowEnrollmentDialog(true);
      return;
    }

    if (isPreviewing || previewEnrollmentMutation.isPending) return;

    if (!skipPaymentMethod) {
      if (
        paymentMethodPreloadStatus !== 'ready' ||
        resolvedPaymentRows.length !== selectedStudents.length
      ) {
        toast.error(
          paymentMethodPreloadError ??
            'Payment methods are still loading. Please wait and try again.',
        );
        return;
      }
    }

    setIsPreviewing(true);
    try {
      if (skipPaymentMethod) {
        const preview = await previewEnrollmentMutation.mutateAsync({
          studioId: studioDetails._id,
          source: 'studio_portal',
          mode: 'bulk',
          requests: [
            {
              entityId: classId,
              entityType: 'class',
              sessionId,
              skipPaymentMethod: true,
              students: selectedStudents.map((student) => ({
                studentId: student._id,
                ...(payingParentByStudent[student._id]
                  ? { payingParentId: payingParentByStudent[student._id] }
                  : {}),
              })),
            },
          ],
        });
        if (isAlreadyEnrolledNoop(preview)) {
          toast.warning(
            preview.message ||
              `Student already enrolled: ${(preview.existingEnrollmentsList ?? []).join(', ')}`,
          );
          return;
        }
        setResolvedPaymentRows([]);
        setEnrollmentPreview({
          amountDueTotal: preview.amountDueTotal ?? 0,
          charges: preview.charges ?? [],
        });
        setShowEnrollmentDialog(true);
        return;
      }

      const preview = await previewEnrollmentMutation.mutateAsync({
        studioId: studioDetails._id,
        source: 'studio_portal',
        mode: 'bulk',
        requests: [
          {
            entityId: classId,
            entityType: 'class',
            sessionId,
            skipPaymentMethod: false,
            students: resolvedPaymentRows.map((row) => ({
              studentId: row.studentId,
              paymentMethodId: row.paymentMethodId,
              ...(payingParentByStudent[row.studentId]
                ? { payingParentId: payingParentByStudent[row.studentId] }
                : {}),
            })),
          },
        ],
      });

      if (isAlreadyEnrolledNoop(preview)) {
        toast.warning(
          preview.message ||
            `Student already enrolled: ${(preview.existingEnrollmentsList ?? []).join(', ')}`,
        );
        return;
      }

      setEnrollmentPreview({
        amountDueTotal: preview.amountDueTotal ?? 0,
        charges: preview.charges ?? [],
      });
      setShowEnrollmentDialog(true);
    } catch (error) {
      console.error('Enrollment preview failed:', error);
      const message =
        error instanceof Error ? error.message : 'Enrollment preview failed.';
      if (!(error && typeof error === 'object' && 'response' in error)) {
        toast.error(message);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleEnrollment = async () => {
    if (!studioDetails?._id) {
      toast.error('Studio details not found');
      return;
    }
    if (
      isEnrolling ||
      enrollMutation.isPending ||
      addStudentToEntityMutation.isPending
    )
      return;

    if (isV2Enrollment && !enrollmentPreview) {
      toast.error('Run enrollment preview before confirming');
      return;
    }

    setIsEnrolling(true);
    try {
      if (!isV2Enrollment) {
        const legacyPaymentMethod =
          !skipPaymentMethod &&
          (paymentMethod === 'card' ||
            paymentMethod === 'us_bank_account' ||
            paymentMethod === 'au_becs_debit')
            ? paymentMethod
            : null;

        const result = await addStudentToEntityMutation.mutateAsync({
          studioId: studioDetails._id,
          entityId: classId,
          studentIds: selectedStudents.map((student) => student._id),
          entityType: 'class',
          paymentMethod: legacyPaymentMethod,
          skipPaymentMethod,
          sessionId,
        });

        if (result.url) {
          window.location.href = result.url;
          return;
        }
        if (result.clientSecret && result.stripePublicApiKey) {
          toast.info('Payment method setup required');
          return;
        }
        // ENR-2625: mutation already warned; keep dialog open so staff can adjust
        // selection (parity with single-enroll modal keep-open behavior).
        if (result.existingEnrollmentsList?.length) {
          return;
        }

        const outcome = getAddStudentToEntityOutcome(result);
        if (outcome.type !== 'success') {
          return;
        }

        setShowEnrollmentDialog(false);
        router.history.back();
        return;
      }

      const basePayload = {
        studioId: studioDetails._id,
        source: 'studio_portal' as const,
        mode: 'bulk' as const,
      };

      if (skipPaymentMethod) {
        const startResult = await enrollMutation.mutateAsync({
          ...basePayload,
          requests: [
            {
              entityId: classId,
              entityType: 'class',
              sessionId,
              skipPaymentMethod: true,
              students: selectedStudents.map((student) => ({
                studentId: student._id,
                ...(payingParentByStudent[student._id]
                  ? { payingParentId: payingParentByStudent[student._id] }
                  : {}),
              })),
            },
          ],
        });
        const outcome = getStartEnrollmentOutcome(startResult);
        if (outcome.type === 'error' || outcome.type === 'already_enrolled') {
          return;
        }
        setShowEnrollmentDialog(false);
        router.history.back();
        return;
      }

      const startResult = await enrollMutation.mutateAsync({
        ...basePayload,
        requests: [
          {
            entityId: classId,
            entityType: 'class',
            sessionId,
            skipPaymentMethod: false,
            students: resolvedPaymentRows.map((row) => ({
              studentId: row.studentId,
              paymentMethodId: row.paymentMethodId,
              ...(payingParentByStudent[row.studentId]
                ? { payingParentId: payingParentByStudent[row.studentId] }
                : {}),
            })),
          },
        ],
      });

      const outcome = getStartEnrollmentOutcome(startResult);
      if (outcome.type === 'error' || outcome.type === 'already_enrolled') {
        return;
      }

      setShowEnrollmentDialog(false);
      router.history.back();
    } catch (error) {
      console.error('Enrollment failed:', error);
      const message =
        error instanceof Error ? error.message : 'Enrollment failed.';
      if (!(error && typeof error === 'object' && 'response' in error)) {
        toast.error(message);
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  const confirmBusy =
    isEnrolling ||
    enrollMutation.isPending ||
    addStudentToEntityMutation.isPending;
  const previewBusy =
    isV2Enrollment && (isPreviewing || previewEnrollmentMutation.isPending);
  const legacyConfirmDisabled =
    !isV2Enrollment &&
    (sessionRegistrationFees.isLoading ||
      isHourlyLastMonthPreviewLoading ||
      hasHourlyLastMonthPreviewError);

  const previewParentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const student of selectedStudents) {
      const parentId = parentIdByStudentId.get(student._id) ?? student.parentId;
      if (parentId) ids.add(parentId);
    }
    return [...ids];
  }, [selectedStudents, parentIdByStudentId]);

  const parentNameQueries = useQueries({
    queries: previewParentIds.map((parentId) => ({
      queryKey: [KEYS.PARENTS, parentId, 'bulk-enroll-confirm'],
      queryFn: () => getParentById(parentId),
      enabled: showEnrollmentDialog && !!parentId && isV2Enrollment,
    })),
  });

  const parentNameById = useMemo(() => {
    const map = new Map<string, string>();
    previewParentIds.forEach((parentId, index) => {
      const parent = parentNameQueries[index]?.data;
      if (parent?.name) {
        map.set(parentId, parent.name);
      } else if (parent?.familyName) {
        map.set(parentId, parent.familyName);
      }
    });
    return map;
  }, [previewParentIds, parentNameQueries]);

  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!showEnrollmentDialog) {
      setExpandedParentIds(new Set());
    }
  }, [showEnrollmentDialog]);

  /** Group preview charges by parent — amounts can differ across families. */
  const parentChargeGroups = useMemo(() => {
    const charges = enrollmentPreview?.charges ?? [];
    if (!charges.length) return [];

    const groups = new Map<
      string,
      {
        parentId: string;
        parentName: string;
        charges: PreviewChargeBreakdown[];
        total: number;
      }
    >();

    for (const charge of charges) {
      const student = selectedStudents.find((s) => s._id === charge.studentId);
      const parentId =
        parentIdByStudentId.get(charge.studentId) ??
        student?.parentId ??
        'unknown';
      const existing = groups.get(parentId);
      const parentName =
        parentNameById.get(parentId) ??
        student?.familyName ??
        (parentId === 'unknown' ? 'Unknown parent' : 'Parent');

      if (existing) {
        existing.charges.push(charge);
        existing.total += charge.amountDueWithTransactionFee ?? 0;
      } else {
        groups.set(parentId, {
          parentId,
          parentName,
          charges: [charge],
          total: charge.amountDueWithTransactionFee ?? 0,
        });
      }
    }

    return [...groups.values()];
  }, [
    enrollmentPreview?.charges,
    selectedStudents,
    parentIdByStudentId,
    parentNameById,
  ]);

  const toggleParentExpanded = (parentId: string) => {
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const currencySymbol = currencyData?.symbol ?? '';

  return (
    <div className=''>
      {canGoBack ? (
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          disabled={!canGoBack}
          onClick={() => {
            router.history.back();
          }}
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>{classData?.title}</h2>
        </Button>
      ) : (
        <Link to='/classes' className='flex w-fit items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>{classData?.title}</h2>
        </Link>
      )}

      <div className='mt-8 border-t'>
        <div className='container max-w-5xl space-y-6 py-6'>
          <Card>
            <CardHeader>
              <CardTitle>Bulk Enroll Students</CardTitle>
              <CardDescription>
                Enroll multiple students at once by selecting them manually or
                by class.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 'selection' ? (
                <StudentSelectionStep
                  onStudentsSelected={handleStudentsSelected}
                />
              ) : (
                <ConfirmationStep students={selectedStudents} />
              )}
            </CardContent>
            <CardFooter className='flex flex-col items-stretch gap-4'>
              {step === 'confirmation' && (
                <>
                  {isV2Enrollment && selectedStudents.length > 0 && (
                    <div className='space-y-3 rounded-md border p-3'>
                      <Label>Paying parent</Label>
                      <p className='text-xs text-muted-foreground'>
                        Defaults to each family&apos;s billing parent. Override
                        per student for this enrollment only.
                      </p>
                      <div className='space-y-3'>
                        {selectedStudents.map((student) => (
                          <div
                            key={student._id}
                            className='grid gap-1 sm:grid-cols-[1fr_min(220px,100%)] sm:items-center sm:gap-3'
                          >
                            <p className='text-sm font-medium'>{student.name}</p>
                            <PayingParentSelect
                              studentId={student._id}
                              value={payingParentByStudent[student._id]}
                              compact
                              disabled={previewBusy || confirmBusy}
                              onChange={(next) => {
                                setPayingParentByStudent((prev) => ({
                                  ...prev,
                                  [student._id]: next,
                                }));
                                setEnrollmentPreview(null);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className='grid gap-1.5'>
                    <Label>Payment Method</Label>
                    {skipPaymentMethod ? (
                      <p className='text-sm text-muted-foreground'>
                        Deferred — parent will pay later
                      </p>
                    ) : paymentMethodPreloadStatus === 'loading' ? (
                      <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <Loader2 className='size-3.5 animate-spin' />
                        Loading saved payment methods…
                      </p>
                    ) : paymentMethodPreloadStatus === 'error' ? (
                      <p className='text-sm text-destructive'>
                        {paymentMethodPreloadError ??
                          'Could not load payment methods.'}
                      </p>
                    ) : paymentMethodPreloadStatus === 'ready' &&
                      resolvedPaymentRows.length > 0 ? (
                      <p className='text-sm'>
                        Using each parent&apos;s default payment method
                        {` (${resolvedPaymentRows.length} ready)`}
                      </p>
                    ) : paymentMethodPreloadStatus === 'idle' ? (
                      <p className='text-sm text-muted-foreground'>
                        Waiting for payment methods…
                      </p>
                    ) : (
                      <p className='text-sm text-muted-foreground'>
                        Using each parent&apos;s default payment method
                      </p>
                    )}
                  </div>

                  <div className='flex items-start space-x-2'>
                    <Checkbox
                      className='mt-0.5'
                      id='bulk-skip-payment-method'
                      checked={skipPaymentMethod}
                      onCheckedChange={(checked) => {
                        if (checked === true) {
                          setPaymentMethod(undefined);
                        } else if (firstAllowedMethod) {
                          setPaymentMethod(firstAllowedMethod);
                        }
                        setSkipPaymentMethod(checked === true);
                        setEnrollmentPreview(null);
                        setResolvedPaymentRows([]);
                        setPaymentMethodPreloadError(null);
                        setPaymentMethodPreloadStatus(
                          checked === true ? 'ready' : 'idle',
                        );
                      }}
                      disabled={previewBusy || confirmBusy}
                    />
                    <Label
                      htmlFor='bulk-skip-payment-method'
                      className='text-sm'
                    >
                      No payment method needed; the parent will pay the
                      outstanding balance later.
                    </Label>
                  </div>
                </>
              )}

              <div className='flex justify-between'>
                {step === 'confirmation' && (
                  <Button
                    variant='outline'
                    onClick={handleBack}
                    disabled={previewBusy || confirmBusy}
                  >
                    Back
                  </Button>
                )}
                {step === 'confirmation' && (
                  <Button
                    onClick={handleConfirm}
                    disabled={
                      previewBusy ||
                      confirmBusy ||
                      (isV2Enrollment &&
                        !skipPaymentMethod &&
                        (paymentMethodPreloadStatus === 'loading' ||
                          paymentMethodPreloadStatus === 'error' ||
                          resolvedPaymentRows.length !==
                            selectedStudents.length))
                    }
                  >
                    {previewBusy ? (
                      <>
                        <Loader2 className='mr-2 size-4 animate-spin' />
                        Calculating charges...
                      </>
                    ) : isV2Enrollment &&
                      !skipPaymentMethod &&
                      paymentMethodPreloadStatus === 'loading' ? (
                      <>
                        <Loader2 className='mr-2 size-4 animate-spin' />
                        Loading payment methods...
                      </>
                    ) : (
                      'Proceed to Enrollment'
                    )}
                  </Button>
                )}
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>

      <Dialog
        open={showEnrollmentDialog}
        onOpenChange={(open) => {
          setShowEnrollmentDialog(open);
          if (!open) {
            // Keep PM / skip choices on the page; clear preview snapshot only.
            setEnrollmentPreview(null);
          }
        }}
      >
        <DialogContent className='flex max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-4 overflow-hidden sm:max-w-lg'>
          <DialogHeader className='shrink-0'>
            <DialogTitle>Confirm Enrollment</DialogTitle>
            <DialogDescription aria-describedby={undefined}></DialogDescription>
          </DialogHeader>

          <div className='min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden text-sm'>
            <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2'>
              <div className='font-medium'>Session:</div>
              <div className='break-words'>
                {getRuntimeSession(classData)?.name}
              </div>

              <div className='font-medium'>Class Name:</div>
              <div className='break-words'>{classData?.title}</div>

              <div className='font-medium'>Students:</div>
              <div>{selectedStudents.length}</div>

              <div className='font-medium'>Billing Type:</div>
              <div>
                {formatBillingCycle(
                  classData?.tuitionBillingCycle,
                  classData?.billingFrequencyDays,
                )}
              </div>
            </div>

            <Separator />

            {isV2Enrollment ? (
              <>
                {parentChargeGroups.length > 0 ? (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Charges by parent
                    </p>
                    {parentChargeGroups.map((group) => {
                      const isOpen = expandedParentIds.has(group.parentId);
                      return (
                        <Collapsible
                          key={group.parentId}
                          open={isOpen}
                          onOpenChange={() =>
                            toggleParentExpanded(group.parentId)
                          }
                        >
                          <CollapsibleTrigger asChild>
                            <button
                              type='button'
                              className='flex w-full items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-left text-amber-950 shadow-sm hover:bg-amber-100/80'
                            >
                              <div className='min-w-0'>
                                <div className='truncate font-medium text-amber-950'>
                                  {group.parentName}
                                </div>
                                <div className='text-xs text-amber-800/70'>
                                  {group.charges.length} student
                                  {group.charges.length === 1 ? '' : 's'}
                                </div>
                              </div>
                              <div className='flex shrink-0 items-center gap-2'>
                                <span className='font-semibold tabular-nums text-amber-950'>
                                  {currencySymbol}
                                  {group.total.toFixed(2)}
                                </span>
                                <ChevronDown
                                  className={cn(
                                    'size-4 text-amber-700/70 transition-transform',
                                    isOpen && 'rotate-180',
                                  )}
                                />
                              </div>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className='space-y-3 px-1 pb-2 pt-2'>
                            {group.charges.map((charge) => (
                              <div
                                key={charge.studentId}
                                className='min-w-0 rounded-md border border-dashed border-slate-200 bg-white px-3 py-2'
                              >
                                <div className='mb-2 flex items-center justify-between gap-2 font-medium'>
                                  <span className='min-w-0 truncate'>
                                    {charge.studentName || 'Student'}
                                  </span>
                                  <span className='shrink-0 tabular-nums'>
                                    {currencySymbol}
                                    {charge.amountDueWithTransactionFee.toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                                <div className='space-y-1.5 text-xs text-muted-foreground'>
                                  {charge.lineItems
                                    .filter(
                                      (line) =>
                                        line.code !== 'transaction_fee' &&
                                        line.code !== 'wallet_credit',
                                    )
                                    .map((line) => (
                                      <div
                                        key={`${charge.studentId}-${line.code}-${line.label}`}
                                        className='grid grid-cols-[minmax(0,1fr)_auto] gap-x-3'
                                      >
                                        <span className='break-words'>
                                          {shortenPreviewLineLabel(line.label)}
                                          {getPreviewLineScheduledSuffix(line)}
                                        </span>
                                        <span className='tabular-nums'>
                                          {currencySymbol}
                                          {line.amount.toFixed(2)}
                                        </span>
                                      </div>
                                    ))}
                                  {(charge.walletCreditApplied ?? 0) > 0 && (
                                    <div className='grid grid-cols-[minmax(0,1fr)_auto] gap-x-3'>
                                      <span>Wallet applied</span>
                                      <span className='tabular-nums'>
                                        −{currencySymbol}
                                        {charge.walletCreditApplied.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                  <div className='grid grid-cols-[minmax(0,1fr)_auto] gap-x-3'>
                                    <span>Subtotal (before fees)</span>
                                    <span className='tabular-nums'>
                                      {currencySymbol}
                                      {charge.amountDue.toFixed(2)}
                                    </span>
                                  </div>
                                  {charge.transactionFeeAmount > 0 && (
                                    <div className='grid grid-cols-[minmax(0,1fr)_auto] gap-x-3'>
                                      <span>Transaction fee</span>
                                      <span className='tabular-nums'>
                                        {currencySymbol}
                                        {charge.transactionFeeAmount.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No charge lines returned from preview.
                  </p>
                )}

                {enrollmentPreview ? (
                  <>
                    <Separator />
                    <div className='flex items-center justify-between gap-3 text-base font-bold'>
                      <span>Final amount to be charged</span>
                      <span className='shrink-0 tabular-nums'>
                        {currencySymbol}
                        {(enrollmentPreview.amountDueTotal ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2'>
                <div className='font-medium'>
                  Session Fee Per Student Total:
                </div>
                <div>
                  {sessionRegistrationFees.isLoading ? (
                    <Loader2 className='size-4 animate-spin' />
                  ) : (
                    formatAmount(
                      sessionRegistrationFees.perStudentTotal,
                      areSessionFeesScheduled,
                    )
                  )}
                </div>

                <div className='font-medium'>Session Fee Per Family Total:</div>
                <div>
                  {sessionRegistrationFees.isLoading ? (
                    <Loader2 className='size-4 animate-spin' />
                  ) : (
                    formatAmount(
                      sessionRegistrationFees.perFamilyTotal,
                      areSessionFeesScheduled,
                    )
                  )}
                </div>

                <div className='font-medium'>Costume Fee:</div>
                <div>
                  {formatAmount(displayCostumeFee, isClassPaymentScheduled)}
                </div>

                <div className='font-medium'>Membership Fee:</div>
                <div>
                  {formatAmount(displayMembershipFee, isClassPaymentScheduled)}
                </div>

                {isHourlyLastMonthCharge ? (
                  <>
                    <div className='col-span-2 font-medium'>
                      Last Month Hourly Tuition
                    </div>
                    {perStudentLastMonthTuition.map((row) => (
                      <div key={row.studentId} className='contents'>
                        <div className='pl-2 text-muted-foreground'>
                          {row.name}
                        </div>
                        <div>
                          {row.isLoading ? (
                            <Loader2 className='size-4 animate-spin' />
                          ) : row.isError ? (
                            <span className='text-destructive'>
                              Unavailable
                            </span>
                          ) : (
                            formatAmount(
                              row.amount ?? 0,
                              isClassPaymentScheduled,
                            )
                          )}
                        </div>
                      </div>
                    ))}
                    <div className='font-medium'>Tuition subtotal:</div>
                    <div>
                      {isHourlyLastMonthPreviewLoading ? (
                        <Loader2 className='size-4 animate-spin' />
                      ) : hasHourlyLastMonthPreviewError ? (
                        <span className='text-destructive'>Unavailable</span>
                      ) : (
                        formatAmount(
                          hourlyLastMonthTuitionTotal ?? 0,
                          isClassPaymentScheduled,
                        )
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className='font-medium'>
                      {`Tuition Fee${isProrated ? ' (Prorated)' : ''}:`}
                    </div>
                    <div>
                      {formatAmount(effectiveTuition, isClassPaymentScheduled)}
                      {isProrated && (
                        <span className='ml-1 text-xs text-muted-foreground line-through'>
                          {currencyData?.symbol}
                          {classData?.tuitionFee ?? 0}
                        </span>
                      )}
                    </div>
                  </>
                )}

                {hasHourlyLastMonthPreviewError && (
                  <p className='col-span-2 text-sm text-destructive'>
                    Could not load last-month tuition for one or more students.
                    Close and try again.
                  </p>
                )}

                {(isHourlyLastMonthCharge
                  ? hourlyLastMonthTotalPayments
                  : totalPaymentsData?.totalPayments) && (
                  <>
                    <div className='font-medium'>Total Payments:</div>
                    <div>
                      {isHourlyLastMonthCharge
                        ? hourlyLastMonthTotalPayments
                        : totalPaymentsData?.totalPayments}
                    </div>
                  </>
                )}

                <Separator className='col-span-2' />

                <div className='font-bold'>Final Amount to be charged:</div>
                <div className='font-bold'>
                  {isHourlyLastMonthPreviewLoading ? (
                    <Loader2 className='size-4 animate-spin' />
                  ) : (
                    <>
                      {currencyData?.symbol}
                      {amountDueNow.toFixed(2)}
                      {isProrated && !isHourlyLastMonthCharge && (
                        <span className='ml-1 text-xs font-normal text-muted-foreground'>
                          (prorated)
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {skipPaymentMethod && (
              <p className='text-sm text-muted-foreground'>
                Payment deferred — parents will pay the outstanding balance
                later. No card will be charged at enrollment.
              </p>
            )}
          </div>

          <DialogFooter className='shrink-0'>
            <Button
              variant='outline'
              onClick={() => {
                setShowEnrollmentDialog(false);
              }}
              disabled={confirmBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnrollment}
              disabled={confirmBusy || legacyConfirmDisabled}
            >
              {confirmBusy ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Enrolling...
                </>
              ) : (
                'Confirm Enrollment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
